// Vulnerability classes behind each attack type the platform detects, mapped to
// MITRE CWE and the OWASP Top 10 (2021). "Targeted" means an attack aimed at that
// weakness was observed; it does not mean the weakness was confirmed exploitable.
export const VULNS = {
  sql_injection: {
    name: "SQL injection",
    weakness: "Improper neutralisation of special elements in SQL commands",
    cwe: "CWE-89",
    owasp: "A03 Injection",
    fix: "Use parameterised queries and validate input.",
  },
  xss: {
    name: "Cross-site scripting",
    weakness: "Improper neutralisation of input in web pages",
    cwe: "CWE-79",
    owasp: "A03 Injection",
    fix: "Encode output and enforce a strict Content-Security-Policy.",
  },
  command_injection: {
    name: "OS command injection",
    weakness: "Improper neutralisation of special elements in OS commands",
    cwe: "CWE-78",
    owasp: "A03 Injection",
    fix: "Never pass user input to a shell; use allow-lists.",
  },
  path_traversal: {
    name: "Path traversal",
    weakness: "Improper limitation of a pathname to a restricted directory",
    cwe: "CWE-22",
    owasp: "A01 Broken Access Control",
    fix: "Canonicalise file paths and serve only from an allowed folder.",
  },
  brute_force: {
    name: "Weak sign-in protection",
    weakness: "Improper restriction of excessive authentication attempts",
    cwe: "CWE-307",
    owasp: "A07 Authentication Failures",
    fix: "Lock out repeat failures, rate-limit sign-in and require MFA.",
  },
  twofa_bypass: {
    name: "Verification-code guessing",
    weakness: "Improper authentication of one-time codes",
    cwe: "CWE-287",
    owasp: "A07 Authentication Failures",
    fix: "Limit code attempts and expire codes quickly.",
  },
  port_scan: {
    name: "Exposed network services",
    weakness: "Resources reachable from untrusted networks",
    cwe: "CWE-668",
    owasp: "A05 Security Misconfiguration",
    fix: "Close unused ports and restrict access by security group.",
  },
  ddos: {
    name: "Resource exhaustion",
    weakness: "Uncontrolled resource consumption",
    cwe: "CWE-400",
    owasp: "",
    fix: "Rate-limit requests and add upstream traffic filtering.",
  },
  privilege_escalation: {
    name: "Privilege escalation",
    weakness: "Improper privilege management",
    cwe: "CWE-269",
    owasp: "A01 Broken Access Control",
    fix: "Apply least privilege and review role assignments.",
  },
  data_exfiltration: {
    name: "Sensitive data exposure",
    weakness: "Exposure of sensitive information to an unauthorised actor",
    cwe: "CWE-200",
    owasp: "A01 Broken Access Control",
    fix: "Restrict outbound traffic and encrypt sensitive data.",
  },
  lateral_movement: {
    name: "Weak internal access control",
    weakness: "Improper access control between internal systems",
    cwe: "CWE-284",
    owasp: "A01 Broken Access Control",
    fix: "Segment the network and restrict internal credentials.",
  },
  web_attack: {
    name: "Unvalidated web input",
    weakness: "Improper input validation",
    cwe: "CWE-20",
    owasp: "A03 Injection",
    fix: "Validate all input on the server side.",
  },
  malware_c2: { name: "Malware infection", weakness: "Compromised host calling a command server", cwe: "", owasp: "", fix: "Isolate the host and block the server address." },
  ransomware: { name: "Ransomware", weakness: "Malicious code encrypting data", cwe: "", owasp: "", fix: "Isolate the host, restore from backup, patch the entry point." },
};

// The bank portal injects its own attack kinds; the log text identifies them precisely.
export function vulnKey(t) {
  const raw = t.raw_log || "";
  if (/command injection/i.test(raw)) return "command_injection";
  if (/path traversal/i.test(raw)) return "path_traversal";
  if (/2fa bypass/i.test(raw)) return "twofa_bypass";
  if (t.attack_type === "botnet_ddos") return "ddos";
  if (t.attack_type === "brute_force_attempt") return "brute_force";
  return t.attack_type;
}

export const isPortalAttack = (t) => /^\[REAL ATTACK\]/.test(t.raw_log || "");
